import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Calendar, ChevronRight } from 'lucide-react'
import { BlogPost } from '../types'
import { formatDate, truncateText } from '../utils/helpers'

interface RelatedPostsProps {
  currentPost?: BlogPost | null
  allPosts: BlogPost[]
}

export function RelatedPosts({ currentPost, allPosts }: RelatedPostsProps) {
  const [related, setRelated] = useState<BlogPost[]>([])

  useEffect(() => {
    if (!allPosts || allPosts.length === 0) return

    // Filter out current post and get posts in same category or just latest posts
    const filtered = allPosts
      .filter((p) => p.slug !== currentPost?.slug)
      .filter((p) => !currentPost || p.category === currentPost.category)
      .slice(0, 3)

    // If not enough related by category, just add latest posts
    if (filtered.length < 3) {
      const more = allPosts
        .filter((p) => p.slug !== currentPost?.slug && !filtered.find((f) => f.slug === p.slug))
        .slice(0, 3 - filtered.length)
      setRelated([...filtered, ...more])
    } else {
      setRelated(filtered)
    }
  }, [currentPost, allPosts])

  if (related.length === 0) return null

  return (
    <div className="bg-gray-50 py-20 border-t border-gray-100">
      <div className="max-w-5xl mx-auto px-6">
        <h2 className="text-3xl font-bold text-gray-900 mb-12">
          {currentPost ? 'Related Articles' : 'Latest from the Blog'}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {related.map((post) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 flex flex-col"
            >
              {post.hero_image && (
                <div className="aspect-video overflow-hidden">
                  <img
                    src={post.hero_image}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
              )}
              <div className="p-6 flex flex-col flex-grow">
                <div className="flex items-center text-xs text-gray-500 mb-3 space-x-4">
                  <div className="flex items-center">
                    <Calendar className="w-3 h-3 mr-1" />
                    {formatDate(post.created_at)}
                  </div>
                  <span className="text-primary-600 font-medium uppercase tracking-wider">
                    {post.category}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
                  {post.title}
                </h3>
                <p className="text-gray-600 text-sm mb-6 line-clamp-3 flex-grow">
                  {post.excerpt}
                </p>
                <div className="flex items-center text-primary-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Read Article <ChevronRight className="w-4 h-4 ml-1" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}
